import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Paper,
  Alert,
  Grid,
} from '@mui/material';
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from 'firebase/auth';
import app from '../firebase';
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import TowerSpeaker from "./../images/TowerSpeaker.png";

const AuthPage = () => {
  const [phone, setPhone] = useState('+91');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [userInfo, setUserInfo] = useState({ name: '' });
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [isSignUp, setIsSignUp] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [nameError, setNameError] = useState('');

  const auth = getAuth(app);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  // Phone number validation
  const validatePhoneNumber = (phoneNumber) => {
    // Ensure it starts with +91
    if (!phoneNumber.startsWith('+91')) {
      return 'Phone number must start with +91';
    }
    
    // Get digits after +91
    const digitsAfterCountryCode = phoneNumber.substring(3);
    
    // Check if it has only digits after +91
    if (digitsAfterCountryCode && !/^\d+$/.test(digitsAfterCountryCode)) {
      return 'Phone number must contain only digits after +91';
    }
    
    // Check if it has exactly 10 digits after +91
    if (digitsAfterCountryCode.length !== 10) {
      return 'Phone number must have 10 digits after +91';
    }
    
    return '';
  };

  // Handle phone number change
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    
    // Always ensure +91 is present and cannot be removed
    if (!value.startsWith('+91')) {
      // If user tries to delete +91, restore it
      if (value.length < 3) {
        setPhone('+91');
        setPhoneError('');
        return;
      }
      // If user tries to change the prefix, keep +91 and extract digits
      const digitsOnly = value.replace(/\D/g, '');
      const limitedDigits = digitsOnly.substring(0, 10);
      const newPhone = '+91' + limitedDigits;
      setPhone(newPhone);
      
      if (newPhone === '+91') {
        setPhoneError('');
      } else {
        const error = validatePhoneNumber(newPhone);
        setPhoneError(error);
      }
    } else {
      // Allow only digits after +91
      const afterPrefix = value.substring(3);
      const digitsOnly = afterPrefix.replace(/\D/g, '');
      // Limit to 10 digits after +91
      const limitedDigits = digitsOnly.substring(0, 10);
      const newPhone = '+91' + limitedDigits;
      setPhone(newPhone);
      
      if (newPhone === '+91') {
        setPhoneError('');
      } else {
        const error = validatePhoneNumber(newPhone);
        setPhoneError(error);
      }
    }
  };

  // Handle key down to prevent deletion of +91 prefix
  const handleKeyDown = (e) => {
    const input = e.target;
    const cursorPosition = input.selectionStart;
    const selectionEnd = input.selectionEnd;
    
    // Prevent backspace or delete if cursor is within the +91 prefix
    if ((e.key === 'Backspace' || e.key === 'Delete') && cursorPosition <= 3) {
      e.preventDefault();
      // Ensure cursor stays at position 3 (after +91)
      setTimeout(() => {
        input.setSelectionRange(3, 3);
      }, 0);
    }
    
    // Prevent arrow keys from moving cursor before position 3
    if ((e.key === 'ArrowLeft' || e.key === 'Home') && cursorPosition < 3) {
      e.preventDefault();
      input.setSelectionRange(3, 3);
    }
    
    // Prevent selection that includes the +91 prefix from being deleted
    if (selectionEnd <= 3 && (e.key === 'Backspace' || e.key === 'Delete')) {
      e.preventDefault();
      setTimeout(() => {
        input.setSelectionRange(3, 3);
      }, 0);
    }
  };

  // Handle focus to ensure cursor is positioned after +91
  const handleFocus = (e) => {
    const input = e.target;
    const cursorPosition = input.selectionStart;
    
    // If cursor is before position 3, move it to position 3
    if (cursorPosition < 3) {
      setTimeout(() => {
        input.setSelectionRange(3, 3);
      }, 0);
    }
  };

  // Handle name change
  const handleNameChange = (e) => {
    const value = e.target.value;
    setUserInfo({ ...userInfo, name: value });
    
    if (value.trim() === '') {
      setNameError('');
    } else if (value.trim().length < 2) {
      setNameError('Name must be at least 2 characters long');
    } else {
      setNameError('');
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setIsSignUp(newValue === 0);
    setPhone('+91');
    setOtp('');
    setIsOtpSent(false);
    setConfirmationResult(null);
    setUserInfo({ name: '' });
    setError('');
    setPhoneError('');
    setNameError('');
  };

  // ✅ Setup reCAPTCHA properly with unique IDs
  useEffect(() => {
    const recaptchaId = isSignUp ? 'recaptcha-signup' : 'recaptcha-login';
    
    // Clear existing reCAPTCHA if any
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (e) {
        console.warn('Previous reCAPTCHA cleanup failed:', e);
      }
      window.recaptchaVerifier = null;
    }

    // Create new reCAPTCHA verifier
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        recaptchaId,
        {
          size: 'invisible',
          callback: (response) => {
            console.log('reCAPTCHA solved:', response);
          },
        }
      );
    }

    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          console.warn('Recaptcha cleanup failed:', e);
        }
        window.recaptchaVerifier = null;
      }
    };
  }, [auth, isSignUp]);

  // ✅ Send OTP
  const handleSendOtp = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validate phone number before sending OTP
      const phoneValidationError = validatePhoneNumber(phone);
      if (phoneValidationError) {
        setPhoneError(phoneValidationError);
        return;
      }
      
      const appVerifier = window.recaptchaVerifier;
      const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`;

      const result = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      setConfirmationResult(result);
      setIsOtpSent(true);
    } catch (error) {
      console.error('Send OTP error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Verify OTP for Sign Up
  const handleVerifyOtpSignUp = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!confirmationResult) return;

      const userCredential = await confirmationResult.confirm(otp);
      const uid = userCredential.user.uid;

      // Store user info in Firestore
      const db = getFirestore(app);
      await setDoc(doc(db, 'users', uid), {
        phone,
        name: userInfo.name,
        createdAt: new Date().toISOString(),
        loyaltyPoints: 0,
      });

      // Use the new session management system
      await login(userCredential.user, false);

      setDialogMessage('Your account has been created successfully! Redirecting to home page...');
      setShowDialog(true);
      resetForm();
      
      // Redirect to home page after a short delay
      setTimeout(() => {
        navigate('/redeem');
      }, 2000);
    } catch (error) {
      console.error('Sign up error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Verify OTP for Login
  const handleVerifyOtpLogin = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!confirmationResult) return;

      const userCredential = await confirmationResult.confirm(otp);
      const uid = userCredential.user.uid;

      // Check if user exists in Firestore
      const db = getFirestore(app);
      const userDoc = await getDoc(doc(db, 'users', uid));
      
      if (userDoc.exists()) {
        // Use the new session management system
        await login(userCredential.user, false);
        
        setDialogMessage('Login successful! Redirecting to home page...');
        setShowDialog(true);
        resetForm();
        
        setTimeout(() => {
          navigate('/redeem');
        }, 2000);
      } else {
        setError('User not found. Please sign up first.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setIsOtpSent(false);
    setPhone('+91');
    setOtp('');
    setUserInfo({ name: '' });
    setConfirmationResult(null);
    setPhoneError('');
    setNameError('');
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    setDialogMessage('');
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 8, p: 4 }}>
        <Grid container spacing={4} alignItems="center" sx={{ display: 'flex !important', flexDirection: 'row !important', justifyContent: 'center !important', alignItems: 'center !important' }}>
          {/* Image on the left */}
          <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
                  component="img"
                  src={TowerSpeaker}
                  alt="Speaker Tower"
                  sx={{ 
                    width: 'auto',
                    maxWidth: { xs: '300px', sm: '400px', md: '500px' },
                    height: { xs: '300px', md: '400px' },
                    objectFit: 'contain',
                    objectPosition: 'bottom',
                  }}
                />
            </Box>
          </Grid>

          {/* Form on the right */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 4, boxShadow: 2, borderRadius: 2 }}>
              <Typography variant="h4" gutterBottom align="center">
                {isSignUp ? 'Sign Up' : 'Login'}
              </Typography>
              
              <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 3 }}>
                {isSignUp ? 'Create your account with phone number' : 'Login with your phone number'}
              </Typography>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={isSignUp ? 0 : 1} onChange={handleTabChange} centered>
                  <Tab label="Sign Up" />
                  <Tab label="Login" />
                </Tabs>
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Phone Input */}
              <TextField
                fullWidth
                label="Phone Number"
                value={phone}
                onChange={handlePhoneChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                disabled={isOtpSent || loading}
                margin="normal"
                required
                error={!!phoneError}
                helperText={phoneError || "Enter 10 digits after +91"}
                placeholder="+91XXXXXXXXXX"
                inputProps={{
                  maxLength: 13 // +91 + 10 digits = 13 characters
                }}
              />

              {/* 🔑 reCAPTCHA container */}
              <div id={isSignUp ? 'recaptcha-signup' : 'recaptcha-login'} />

              {/* OTP Input */}
              {isOtpSent && (
                <TextField
                  fullWidth
                  label="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  margin="normal"
                  disabled={loading}
                  required
                />
              )}

              {/* Name Input (only for Sign Up) */}
              {isSignUp && (
                <TextField
                  fullWidth
                  label="Name"
                  value={userInfo.name}
                  onChange={handleNameChange}
                  margin="normal"
                  disabled={isOtpSent || loading}
                  required
                  error={!!nameError}
                  helperText={nameError || "Enter your full name"}
                />
              )}

              {/* Buttons */}
              <Box sx={{ mt: 3 }}>
                {!isOtpSent ? (
                  <Button 
                    onClick={handleSendOtp} 
                    variant="contained" 
                    fullWidth
                    sx={{ backgroundColor: "#c1ec4b", color: "black" }}
                    disabled={!phone || loading || !!phoneError || (isSignUp && (!userInfo.name || !!nameError))}
                    size="large"
                  >
                    {loading ? 'Sending...' : 'Send OTP'}
                  </Button>
                ) : (
                  <Button 
                    onClick={isSignUp ? handleVerifyOtpSignUp : handleVerifyOtpLogin} 
                    variant="contained" 
                    fullWidth
                    sx={{ backgroundColor: "#c1ec4b", color: "black" }}
                    disabled={!otp || (isSignUp && (!userInfo.name || !!nameError)) || loading}
                    size="large"
                  >
                    {loading ? 'Verifying...' : (isSignUp ? 'Sign Up' : 'Login')}
                  </Button>
                )}
              </Box>

              {/* Back Button */}
              {isOtpSent && (
                <Box sx={{ mt: 2 }}>
                  <Button 
                    onClick={resetForm} 
                    variant="text" 
                    fullWidth
                    disabled={loading}
                  >
                    Back to Phone Number
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Success Dialog */}
      <Dialog open={showDialog} onClose={handleDialogClose}>
        <DialogTitle>{isSignUp ? 'Sign Up Successful' : 'Login Successful'}</DialogTitle>
        <DialogContent>
          <Typography>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            handleDialogClose();
            navigate('/');
          }}>
            Go to Home
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AuthPage;
